// modules/ads-stats/AccountFilterModal.tsx
// Модалка для введення списку ID рекламних кабінетів та їх валідації

import React, { useState, useCallback } from 'react';
import { api } from '../../core/apiClient.ts';

interface KnownAccount {
  id: string;
  name: string | null;
}

interface AccountFilterModalProps {
  isOpen: boolean;
  currentFilter: string[] | null;
  onApply: (ids: string[] | null) => void;
  onClose: () => void;
}

function parseIdsRaw(text: string): string[] {
  return text
    .split(/[\n,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseIdsUnique(text: string): string[] {
  return Array.from(new Set(parseIdsRaw(text)));
}

export function AccountFilterModal({
  isOpen,
  currentFilter,
  onApply,
  onClose,
}: AccountFilterModalProps) {
  const [text, setText] = useState<string>(currentFilter ? currentFilter.join('\n') : '');
  const [knownAccounts, setKnownAccounts] = useState<KnownAccount[]>([]);
  const [verifyResult, setVerifyResult] = useState<{
    found: KnownAccount[];
    notFound: string[];
  } | null>(null);
  const [dedupInfo, setDedupInfo] = useState<{ total: number; unique: number } | null>(null);
  const [verifying, setVerifying] = useState(false);

  const handleVerify = useCallback(async () => {
    const rawIds = parseIdsRaw(text);
    const uniqueIds = parseIdsUnique(text);
    
    if (rawIds.length !== uniqueIds.length) {
      setDedupInfo({ total: rawIds.length, unique: uniqueIds.length });
    } else {
      setDedupInfo(null);
    }

    if (uniqueIds.length === 0) {
      setVerifyResult({ found: [], notFound: [] });
      return;
    }

    setVerifying(true);
    try {
      const accounts = await api.get<KnownAccount[]>('/api/ads-stats/known-accounts');
      setKnownAccounts(accounts);

      const knownMap = new Map(accounts.map((a) => [a.id, a]));
      const found: KnownAccount[] = [];
      const notFound: string[] = [];

      for (const id of uniqueIds) {
        const acct = knownMap.get(id);
        if (acct) {
          found.push(acct);
        } else {
          notFound.push(id);
        }
      }

      setVerifyResult({ found, notFound });
    } catch {
      setVerifyResult(null);
    } finally {
      setVerifying(false);
    }
  }, [text]);

  const handleApply = useCallback(() => {
    const ids = parseIdsUnique(text);
    onApply(ids.length > 0 ? ids : null);
    onClose();
  }, [text, onApply, onClose]);

  const handleReset = useCallback(() => {
    onApply(null);
    setText('');
    setVerifyResult(null);
    setDedupInfo(null);
    onClose();
  }, [onApply, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          width: 'min(520px, 90vw)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          padding: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
            Фільтр по кабінетах
          </h2>
          <button
            className="btn btn--ghost"
            style={{ padding: 'var(--space-1) var(--space-2)', fontSize: '1.25rem', lineHeight: 1 }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Description */}
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
          Введи ID кабінетів — по одному на рядок. Якщо порожньо — звіт по всіх кабінетах.
        </p>

        {/* Textarea */}
        <textarea
          id="account-filter-textarea"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setVerifyResult(null);
            setDedupInfo(null);
          }}
          placeholder={`1591745112523168\n2086189782107124\n...`}
          rows={6}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />

        {/* Dedup Info */}
        {dedupInfo && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning)', marginTop: '-8px' }}>
            Введено {dedupInfo.total} рядків, після видалення дублікатів: {dedupInfo.unique} унікальних ID
          </div>
        )}

        {/* Verify button */}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            id="verify-accounts-btn"
            className="btn btn--secondary"
            style={{ flex: 1 }}
            onClick={handleVerify}
            disabled={verifying}
          >
            {verifying ? '⏳ Перевіряю...' : '🔍 Перевірити'}
          </button>
        </div>

        {/* Verify result */}
        {verifyResult && (
          <div
            style={{
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              fontSize: '0.8125rem',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {verifyResult.found.length > 0 && (
              <div>
                <span style={{ color: '#4ade80', fontWeight: 600 }}>
                  ✓ Знайдено {verifyResult.found.length}:
                </span>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                  {verifyResult.found.map((a) => (
                    <li key={a.id}>
                      <code style={{ fontSize: '0.75rem', color: 'var(--color-text)' }}>{a.id}</code>
                      {a.name && (
                        <span style={{ color: 'var(--color-text-muted)' }}> — {a.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {verifyResult.notFound.length > 0 && (
              <div>
                <span style={{ color: '#f87171', fontWeight: 600 }}>
                  ✗ Не знайдено {verifyResult.notFound.length}:
                </span>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                  {verifyResult.notFound.map((id) => (
                    <li key={id}>
                      <code style={{ fontSize: '0.75rem', color: '#f87171' }}>{id}</code>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                        {' '}— не в базі (помилка ID або кабінет не синканий)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {verifyResult.found.length === 0 && verifyResult.notFound.length === 0 && (
              <span style={{ color: 'var(--color-text-muted)' }}>Не введено жодного ID</span>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
          <button
            id="reset-account-filter-btn"
            className="btn btn--ghost"
            style={{ flex: '0 0 auto', fontSize: '0.8125rem' }}
            onClick={handleReset}
          >
            🔓 Скинути (всі акаунти)
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn--secondary" onClick={onClose}>
            Скасувати
          </button>
          <button
            id="apply-account-filter-btn"
            className="btn btn--primary"
            onClick={handleApply}
          >
            Застосувати
          </button>
        </div>
      </div>
    </>
  );
}
